<template>
  <div class="hitl-full-embed-demo">
    <section class="embed-purpose" aria-label="Full HITL embed purpose">
      <h2 class="embed-purpose__title">Full HITL embed</h2>
      <p class="embed-purpose__text">
        After the same
        <code class="embed-purpose__code">or</code>
        cookie / JWT auth flow as Micro HITL, the host page embeds the complete HITL app at
        <code class="embed-purpose__code">{{ hitlOrigin }}</code>.
        Session cookies scoped to
        <code class="embed-purpose__code">{{ cookieScope }}</code>
        apply to the HITL origin automatically — no script bundles on the host.
        The parent talks to the iframe via
        <code class="embed-purpose__code">postMessage</code>
        using the same
        <code class="embed-purpose__code">HITL::…</code>
        event bus as Action Desk cards.
      </p>
    </section>

    <div class="hitl-full-embed-stage">
      <section class="hitl-full-embed-auth" aria-label="Start full HITL embed">
        <div class="hitl-full-embed-auth__toolbar">
          <button
            type="button"
            class="hitl-full-embed-auth__toggle"
            :aria-expanded="authSectionExpanded"
            aria-controls="hitl-full-embed-auth-panel"
            @click="authSectionExpanded = !authSectionExpanded"
          >
            <span
              class="hitl-full-embed-auth__chevron"
              :class="{ 'hitl-full-embed-auth__chevron--open': authSectionExpanded }"
              aria-hidden="true"
            />
            <span class="hitl-full-embed-auth__toolbar-title">Auth</span>
          </button>
          <p class="hitl-full-embed-auth__toolbar-status" role="status">
            {{ orTokenStatusLine }}
          </p>
          <div class="hitl-full-embed-auth__toolbar-actions">
            <button
              type="button"
              class="hitl-full-embed-auth__btn"
              :disabled="orLogoutInProgress"
              @click="logoutOrSession"
            >
              {{ orLogoutInProgress ? 'Signing out…' : 'Log out' }}
            </button>
            <button
              type="button"
              class="hitl-full-embed-auth__btn hitl-full-embed-auth__btn--primary"
              :disabled="!canStartEmbed"
              @click="startEmbed(false)"
            >
              Start HITL
            </button>
            <button
              type="button"
              class="hitl-full-embed-auth__btn hitl-full-embed-auth__btn--primary"
              :disabled="!canStartEmbed"
              @click="startEmbed(true)"
            >
              Start HitL Dev
            </button>
          </div>
        </div>

        <div
          id="hitl-full-embed-auth-panel"
          v-show="authSectionExpanded"
          class="hitl-full-embed-auth__panel"
        >
          <p class="hitl-full-embed-auth__help">
            Paste a JWT if the session check above did not find a valid
            <code class="embed-purpose__code">or</code>
            cookie. The widget calls the shepherd-vet auth API with
            <code class="embed-purpose__code">POST { token }</code>
            , then sets
            <code class="embed-purpose__code">or</code>
            /
            <code class="embed-purpose__code">or_expire</code>
            before starting the embed.
          </p>
          <label class="hitl-full-embed-auth__field">
            <span class="hitl-full-embed-auth__label">JWT</span>
            <textarea
              v-model="jwtInput"
              class="hitl-full-embed-auth__input"
              rows="3"
              autocomplete="off"
              spellcheck="false"
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9…"
            />
          </label>
          <p class="hitl-full-embed-auth__logout-hint">
            <strong>Log out</strong>
            revokes the OneReach session token and clears cookies; the iframe stays mounted.
          </p>
        </div>
      </section>

      <section
        v-if="embedStarted"
        class="hitl-full-embed-demo-cards"
        aria-label="Demo contacts"
      >
        <div class="hitl-full-embed-demo-cards__toolbar">
          <button
            type="button"
            class="hitl-full-embed-demo-cards__toggle"
            :aria-expanded="contactsSectionExpanded"
            aria-controls="hitl-full-embed-contacts-panel"
            @click="contactsSectionExpanded = !contactsSectionExpanded"
          >
            <span
              class="hitl-full-embed-demo-cards__chevron"
              :class="{ 'hitl-full-embed-demo-cards__chevron--open': contactsSectionExpanded }"
              aria-hidden="true"
            />
            <span class="hitl-full-embed-demo-cards__toolbar-title">Mock CRM contacts</span>
          </button>
          <p class="hitl-full-embed-demo-cards__toolbar-status" role="status">
            {{ contactsSectionStatusLine }}
          </p>
        </div>
        <div
          id="hitl-full-embed-contacts-panel"
          v-show="contactsSectionExpanded"
          class="hitl-full-embed-demo-cards__panel"
        >
        <p class="hitl-full-embed-demo-cards__help">
          Buttons send
          <code class="embed-purpose__code">HITL::select-contact</code>
          and
          <code class="embed-purpose__code">HITL::start-conversation</code>
          into the iframe via
          <code class="embed-purpose__code">contentWindow.postMessage</code>
          . Incoming
          <code class="embed-purpose__code">HITL::…</code>
          events (e.g.
          <code class="embed-purpose__code">HITL::app-ready</code>
          ) are logged on the host
          <code class="embed-purpose__code">window</code>
          listener.
        </p>
        <label class="hitl-full-embed-demo-cards__sms-prefill">
          <span class="hitl-full-embed-demo-cards__sms-label">Initial SMS message (optional)</span>
          <textarea
            v-model="smsInitialMessage"
            class="hitl-full-embed-demo-cards__sms-input"
            rows="2"
            maxlength="2000"
            placeholder="e.g. Hi, following up on your request…"
          />
        </label>
        <ul class="hitl-full-embed-demo-cards__list">
          <li
            v-for="c in mockContacts"
            :key="c.id"
            class="hitl-full-embed-demo-card"
          >
            <div class="hitl-full-embed-demo-card__main">
              <span class="hitl-full-embed-demo-card__name">{{ c.name }}</span>
              <span class="hitl-full-embed-demo-card__meta">{{ c.detail }}</span>
              <span class="hitl-full-embed-demo-card__id">id: {{ c.id }}</span>
            </div>
            <div class="hitl-full-embed-demo-card__actions">
              <button
                type="button"
                class="hitl-full-embed-demo-card__btn hitl-full-embed-demo-card__btn--primary"
                :disabled="!hitlReady"
                @click="selectContact(c.id)"
              >
                Open in HITL
              </button>
              <button
                type="button"
                class="hitl-full-embed-demo-card__btn"
                :disabled="!hitlReady"
                @click="startConversation(c.id, 'text', c.phone)"
              >
                New SMS
              </button>
              <button
                type="button"
                class="hitl-full-embed-demo-card__btn"
                :disabled="!hitlReady"
                @click="startConversation(c.id, 'text')"
              >
                New SMS (no identifier)
              </button>
              <button
                type="button"
                class="hitl-full-embed-demo-card__btn"
                :disabled="!hitlReady"
                @click="startConversation(c.id, 'voice', c.phone)"
              >
                Voice
              </button>
            </div>
            </li>
        </ul>
        </div>
      </section>

      <div class="hitl-full-embed-stage__shell" aria-label="Full HITL embed region">
        <p v-if="!embedStarted" class="hitl-full-embed-stage__placeholder">
          <template v-if="orTokenCheckState === 'checking'">
            Checking the
            <code class="embed-purpose__code">or</code>
            session cookie…
          </template>
          <template v-else-if="orTokenCheckState === 'allowed'">
            Click
            <code class="embed-purpose__code">Start HITL</code>
            — your
            <code class="embed-purpose__code">or</code>
            cookie is valid.
          </template>
          <template v-else>
            Expand
            <strong>Auth</strong>
            to paste a JWT, then click
            <code class="embed-purpose__code">Start HITL</code>
            .
          </template>
        </p>
        <p v-else-if="!embedInitialized" class="hitl-full-embed-stage__placeholder">
          Authorizing and loading HITL…
        </p>
        <HitlFullEmbedWidget
          v-if="embedStarted"
          :jwt-token="committedJwt"
          :dev="embedDev"
          @ready="onEmbedBridgeReady"
          @hitl-event="onHitlHostEvent"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { COOKIE_SCOPE, HITL_ORIGIN, MOCK_CONTACTS } from '@/constants/env.js';
import HitlFullEmbedWidget from './HitlFullEmbedWidget.vue';
import { isOrCookieTokenAllowed, logoutOrCookieSession } from '@/utils/orCookieSession.js';

export default {
  name: 'HitlFullEmbedDemo',
  components: { HitlFullEmbedWidget },
  data() {
    return {
      hitlOrigin: HITL_ORIGIN,
      cookieScope: COOKIE_SCOPE,
      callbackId: 'full-hitl-embed-demo',
      authSectionExpanded: false,
      contactsSectionExpanded: false,
      orLogoutInProgress: false,
      orTokenCheckState: 'checking',
      jwtInput: '',
      committedJwt: '',
      /** When true, the iframe URL includes `dev=true`. */
      embedDev: false,
      embedStarted: false,
      embedInitialized: false,
      hitlBridge: null,
      /** Set when `HITL::app-ready` is received; gates contact actions until the app is live. */
      hitlReady: false,
      smsInitialMessage: '',
      mockContacts: MOCK_CONTACTS,
    };
  },
  computed: {
    jwtInputTrimmed() {
      return (this.jwtInput || '').trim();
    },
    orTokenStatusLine() {
      switch (this.orTokenCheckState) {
        case 'checking':
          return 'Checking or session cookie…';
        case 'allowed':
          return 'Valid or session — JWT optional.';
        default:
          return 'No active or session — expand to paste a JWT.';
      }
    },
    canStartEmbed() {
      if (this.embedStarted) return false;
      if (this.orTokenCheckState === 'checking') return false;
      if (this.orTokenCheckState === 'allowed') return true;
      return this.jwtInputTrimmed.length > 0;
    },
    contactsSectionStatusLine() {
      if (!this.hitlReady) {
        return 'Waiting for HITL::app-ready…';
      }
      return `${this.mockContacts.length} contacts — ready.`;
    },
  },
  mounted() {
    this.probeOrCookieSession();
  },
  methods: {
    async probeOrCookieSession() {
      this.orTokenCheckState = 'checking';
      const ok = await isOrCookieTokenAllowed();
      this.orTokenCheckState = ok ? 'allowed' : 'denied';
    },

    startEmbed(dev = false) {
      if (!this.canStartEmbed) return;
      this.committedJwt = this.jwtInputTrimmed;
      this.embedDev = dev === true;
      this.embedStarted = true;
    },

    async logoutOrSession() {
      if (this.orLogoutInProgress) return;
      this.orLogoutInProgress = true;
      try {
        await logoutOrCookieSession();
        await this.probeOrCookieSession();
      } finally {
        this.orLogoutInProgress = false;
      }
    },

    onEmbedBridgeReady(bridge) {
      this.hitlBridge = bridge;
      this.embedInitialized = true;
      this.hitlReady = false;
    },

    onHitlHostEvent({ message }) {
      if (message === 'HITL::app-ready') {
        this.hitlReady = true;
      }
    },

    selectContact(contactId) {
      const send = this.hitlBridge?.send;
      if (!send) return;

      send('HITL::select-contact', {
        callbackId: this.callbackId,
        params: { contactId },
      });
    },

    startConversation(contactId, type, phone) {
      const send = this.hitlBridge?.send;
      if (!send) return;

      const message = (this.smsInitialMessage || '').trim();
      send('HITL::start-conversation', {
        contactId,
        type,
        params: {
          ...(message ? { message } : {}),
          ...(phone ? { identifier: phone } : {}),
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.hitl-full-embed-demo {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
  background: linear-gradient(180deg, #e8ecf4 0%, #f2f5f9 28%, #fafbfc 100%);
}

.embed-purpose {
  flex-shrink: 0;
  padding: 28px 24px 16px;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;

  &__title {
    margin: 0 0 14px;
    font-size: clamp(1.45rem, 2.6vw, 1.85rem);
    font-weight: 700;
    color: #141823;
    letter-spacing: -0.03em;
    line-height: 1.22;
  }

  &__text {
    margin: 0;
    font-size: 15px;
    line-height: 1.6;
    color: #4a5568;
    max-width: 56rem;
  }

  &__code {
    font-size: 12px;
    padding: 1px 6px;
    border-radius: 4px;
    background: #eef2f7;
    color: #1a3a5c;
  }
}

.hitl-full-embed-stage {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  padding: 0 24px 24px;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  gap: 16px;

  &__shell {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-self: center;
    box-sizing: border-box;
    width: calc(100vw - 100px);
    max-width: calc(100vw - 100px);
    min-height: min(72vh, 900px);
    border: 1px solid #cfd8e6;
    border-radius: 10px;
    box-shadow:
      0 1px 2px rgba(15, 23, 42, 0.05),
      0 4px 14px rgba(15, 23, 42, 0.06);
    background: #fff;
    overflow: hidden;
    position: relative;
  }

  &__placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 24px;
    font-size: 14px;
    line-height: 1.5;
    color: #64748b;
    text-align: center;
    pointer-events: none;
    z-index: 0;
  }
}

.hitl-full-embed-auth {
  flex-shrink: 0;
  padding: 14px 16px;
  border: 1px solid #c5d4e8;
  border-radius: 10px;
  background: #f0f6fc;

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 14px;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 4px 8px 4px 4px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font: inherit;
    color: #1a3a5c;
    cursor: pointer;

    &:hover {
      background: rgba(25, 118, 210, 0.08);
    }

    &:focus-visible {
      outline: 2px solid #1976d2;
      outline-offset: 2px;
    }
  }

  &__chevron {
    display: block;
    width: 0.4em;
    height: 0.4em;
    border-right: 2px solid #334155;
    border-bottom: 2px solid #334155;
    transform: rotate(-45deg);
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(45deg);
    }
  }

  &__toolbar-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  &__toolbar-status {
    flex: 1 1 200px;
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: #475569;
    min-width: 0;
  }

  &__toolbar-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: auto;
  }

  &__panel {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #c5d4e8;
  }

  &__help,
  &__logout-hint {
    margin: 0 0 12px;
    font-size: 13px;
    line-height: 1.5;
    color: #4a5568;
  }

  &__logout-hint {
    margin-bottom: 0;
    font-size: 12px;
    color: #64748b;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 42rem;
  }

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: #444;
  }

  &__input {
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid #a8b8d0;
    border-radius: 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    line-height: 1.4;
    resize: vertical;
    min-height: 4rem;

    &::placeholder {
      color: #94a3b8;
    }

    &:disabled {
      background: #e8eef5;
      color: #64748b;
    }
  }

  &__btn {
    padding: 8px 16px;
    border: 1px solid #c5c5c5;
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #f5f5f5;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--primary {
      border-color: #1976d2;
      background: #1976d2;
      color: #fff;

      &:hover:not(:disabled) {
        background: #1565c0;
        border-color: #1565c0;
      }
    }
  }
}

.hitl-full-embed-demo-cards {
  flex-shrink: 0;
  padding: 14px 16px;
  border: 1px solid #c5d4e8;
  border-radius: 10px;
  background: #f0f6fc;

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 14px;
  }

  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 4px 8px 4px 4px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font: inherit;
    color: #1a3a5c;
    cursor: pointer;

    &:hover {
      background: rgba(25, 118, 210, 0.08);
    }

    &:focus-visible {
      outline: 2px solid #1976d2;
      outline-offset: 2px;
    }
  }

  &__chevron {
    display: block;
    width: 0.4em;
    height: 0.4em;
    border-right: 2px solid #334155;
    border-bottom: 2px solid #334155;
    transform: rotate(-45deg);
    transition: transform 0.15s ease;

    &--open {
      transform: rotate(45deg);
    }
  }

  &__toolbar-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  &__toolbar-status {
    flex: 1 1 200px;
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: #475569;
    min-width: 0;
  }

  &__panel {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #c5d4e8;
  }

  &__help {
    margin: 0 0 14px;
    font-size: 13px;
    line-height: 1.5;
    color: #4a5568;
    max-width: 56rem;
  }

  &__sms-prefill {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0 0 14px;
    max-width: 42rem;
  }

  &__sms-label {
    font-size: 13px;
    font-weight: 500;
    color: #444;
  }

  &__sms-input {
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid #c5c5c5;
    border-radius: 6px;
    font: inherit;
    font-size: 13px;
    line-height: 1.4;
    resize: vertical;
    min-height: 2.75rem;

    &::placeholder {
      color: #888;
    }
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__hint {
    margin: 12px 0 0;
    font-size: 13px;
    color: #666;
  }
}

.hitl-full-embed-demo-card {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #d8e0ea;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  &__main {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    flex: 1 1 200px;
  }

  &__name {
    font-weight: 600;
    font-size: 15px;
    color: #1a1a1a;
  }

  &__meta {
    font-size: 13px;
    color: #555;
  }

  &__id {
    font-size: 11px;
    font-family: ui-monospace, monospace;
    color: #888;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    flex: 0 1 auto;
  }

  &__btn {
    padding: 6px 12px;
    border: 1px solid #c5c5c5;
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #f5f5f5;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--primary {
      border-color: #1976d2;
      color: #1976d2;
      font-weight: 600;

      &:hover:not(:disabled) {
        background: #e3f2fd;
      }
    }
  }
}
</style>
